import React from 'react';
import { Link } from 'react-router-dom';
import {
  Wind, ArrowLeft, Phone, Mail, MapPin, MessageCircle, ShieldCheck, Award,
  TrendingUp, Leaf, Flame, Zap, Factory, HardHat, Settings, Users, CheckCircle,
  Sparkles, Target, Heart, ClipboardCheck, Wrench, FlaskConical, AlertTriangle,
  Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo } from '../mock';

/* Brochure-style page wrapper — diagonal cyan accent + logo watermark */
const BrochurePage = ({ pageNo, totalPages, children, dark = false, testId }) => (
  <section
    data-testid={testId}
    className={`relative min-h-[100vh] md:min-h-[90vh] overflow-hidden ${dark ? 'bg-slate-950 text-white' : 'bg-white text-slate-900'}`}
  >
    {/* Diagonal accent overlay */}
    <div
      className={`absolute inset-y-0 right-0 w-1/2 md:w-2/5 ${dark ? 'opacity-30' : 'opacity-90'}`}
      style={{
        clipPath: 'polygon(35% 0, 100% 0, 100% 100%, 0% 100%)',
        background: dark
          ? 'linear-gradient(135deg, #0e7490 0%, #0369a1 60%, #1e3a8a 100%)'
          : 'linear-gradient(135deg, #ecfeff 0%, #cffafe 50%, #a5f3fc 100%)',
      }}
    />
    {/* Page content */}
    <div className="relative z-10 container mx-auto max-w-6xl px-6 md:px-10 py-20 md:py-24">
      {children}
    </div>
    {/* Bottom watermark logo */}
    <div className="absolute bottom-6 right-6 z-20 flex items-center gap-2 opacity-80">
      <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-md flex items-center justify-center shadow">
        <Wind className="h-5 w-5 text-white" />
      </div>
      <div className="flex flex-col leading-none">
        <span className={`text-xs font-bold tracking-wide ${dark ? 'text-white' : 'text-slate-800'}`}>SKY CONTROL</span>
        <span className={`text-[10px] tracking-widest ${dark ? 'text-cyan-300' : 'text-cyan-600'}`}>SOLUTION & TRADING</span>
      </div>
    </div>
    {/* Page number */}
    {pageNo && (
      <div className={`absolute bottom-6 left-6 z-20 text-xs font-mono ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
        {String(pageNo).padStart(2, '0')} / {String(totalPages).padStart(2, '0')}
      </div>
    )}
  </section>
);

const PageTitle = ({ children, dark = false }) => (
  <h2 className={`text-3xl md:text-5xl font-extrabold mb-8 tracking-tight ${dark ? 'text-amber-300' : 'text-amber-500'}`}>
    {children}
  </h2>
);

/* Image with caption + subtle border + onError fallback */
const PLACEHOLDER = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600">
    <defs>
      <linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#0e7490"/>
        <stop offset="100%" stop-color="#1e3a8a"/>
      </linearGradient>
      <pattern id="p" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M0 40L40 0M-10 10L10 -10M30 50L50 30" stroke="rgba(255,255,255,0.08)" stroke-width="2"/>
      </pattern>
    </defs>
    <rect width="800" height="600" fill="url(#g)"/>
    <rect width="800" height="600" fill="url(#p)"/>
    <text x="50%" y="50%" font-family="system-ui, sans-serif" font-size="32" font-weight="700" fill="rgba(255,255,255,0.6)" text-anchor="middle" dy="0.35em">Sky Control · Industrial Works</text>
  </svg>`
);

const PhotoCard = ({ src, alt, caption, className = '' }) => (
  <figure className={`relative rounded-xl overflow-hidden shadow-xl border border-slate-200 group bg-slate-100 ${className}`}>
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
      className="w-full h-full object-cover aspect-[4/3] group-hover:scale-105 transition-transform duration-700"
    />
    {caption && (
      <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-slate-900/85 to-transparent text-white text-xs md:text-sm font-medium px-4 py-3">
        {caption}
      </figcaption>
    )}
  </figure>
);

/* Image URLs — confirmed-loading Unsplash photos depicting industrial / construction work */
const IMG = {
  worker: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80&auto=format',     // workers on construction site (rebar/PPE)
  crew: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1200&q=80&auto=format',       // construction crew with PPE
  electrical: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=1200&q=80&auto=format', // power lines / electrical infrastructure
  piping: 'https://images.unsplash.com/photo-1581092335397-9583eb92d232?w=1200&q=80&auto=format',     // industrial machinery / piping
};

/* Business Services list */
const businessServices = [
  { icon: Wrench, title: 'Mechanical, Piping & Steel Structure Fabrication & Erection' },
  { icon: Zap, title: 'Electrical Installation Works' },
  { icon: AlertTriangle, title: 'Insulation, Abrasive Blasting and Painting Works' },
  { icon: FlaskConical, title: 'Maintenance and Shutdown Services for Oil & Chemical Plants' },
  { icon: Users, title: 'Provision of Skilled Labor Resources' },
];

/* Major Clients */
const clients = [
  { name: 'Veolia Energy Pte Ltd', sector: 'Energy & District Cooling', icon: Leaf },
  { name: 'City Gas', sector: 'Gas Distribution', icon: Flame },
  { name: 'Brightsun Electrical Pte Ltd', sector: 'Electrical Infrastructure', icon: Zap },
  { name: 'Lexington Enterprises Pte Ltd', sector: 'Industrial Projects', icon: Factory },
];

/* Core Services bulleted list */
const coreServices = [
  'Mechanical equipment erection works',
  'Piping fabrication and installation',
  'HVAC (Heating, Ventilation and Air Conditioning) system works',
  'Electrical and instrumentation system installation and testing',
  'Painting and coating works',
  'Underground piping works',
  'Chemical cleaning works',
  'Demolition and removal of existing structures',
  'Manpower supply: Skilled workers and general civil workers',
];

/* Key Projects */
const projects = [
  { no: 1, title: 'District Cooling Plant — Piping Works', client: 'Veolia Energy', scope: 'Process piping fabrication & installation', duration: '2024', status: 'Completed' },
  { no: 2, title: 'Gas Distribution Network Maintenance', client: 'City Gas', scope: 'Preventive & corrective maintenance', duration: '2024 – 2025', status: 'Ongoing' },
  { no: 3, title: 'Electrical Infrastructure Upgrade', client: 'Brightsun Electrical', scope: 'Cable laying, dressing & termination', duration: '2024', status: 'Completed' },
  { no: 4, title: 'Plant Expansion — Mechanical Works', client: 'Lexington Enterprises', scope: 'Equipment erection & steel structure', duration: '2025', status: 'Ongoing' },
  { no: 5, title: 'Energy Recovery Facility — Shutdown Support', client: 'Veolia Energy', scope: 'Shutdown maintenance & manpower supply', duration: '2025', status: 'Ongoing' },
  { no: 6, title: 'HVAC System Installation & Commissioning', client: 'Confidential — Industrial Client', scope: 'HVAC piping, ducting, commissioning', duration: '2025 – 2026', status: 'Upcoming' },
];

const Profile = () => {
  const TOTAL = 11;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="profile-page">
      {/* Sticky Back Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="container mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" data-testid="logo-home-link" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-md flex items-center justify-center shadow">
              <Wind className="h-5 w-5 text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-sm font-bold text-slate-800">SKY CONTROL</span>
              <span className="text-[10px] tracking-widest text-cyan-600">SOLUTION & TRADING</span>
            </div>
          </Link>
          <Link to="/" data-testid="back-home-link">
            <Button variant="outline" size="sm" className="border-cyan-600 text-cyan-700 hover:bg-cyan-50">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
            </Button>
          </Link>
        </div>
      </div>

      <div className="pt-14">

        {/* ============================================== */}
        {/* PAGE 1 — COVER                                  */}
        {/* ============================================== */}
        <BrochurePage pageNo={1} totalPages={TOTAL} dark testId="page-cover">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-400/40 bg-cyan-500/10 text-cyan-300 text-xs font-semibold tracking-widest mb-8">
                COMPANY PROFILE · 2026
              </div>
              <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6">
                Sky Control <span className="text-cyan-400">Solution & Trading</span> Pte Ltd
              </h1>
              <p className="text-base md:text-lg text-slate-300 mb-10 max-w-xl">
                Integrated Industrial Services · Process Construction · Maintenance · Manpower Supply
              </p>

              <div className="space-y-3 text-sm md:text-base">
                <div className="flex items-start gap-3"><Building2 className="h-5 w-5 text-cyan-400 mt-0.5" /><span><span className="text-slate-400">UEN:</span> Registered in Singapore</span></div>
                <div className="flex items-start gap-3"><MapPin className="h-5 w-5 text-cyan-400 mt-0.5" /><span>{companyInfo.address}</span></div>
                <div className="flex items-start gap-3"><Mail className="h-5 w-5 text-cyan-400 mt-0.5" /><a href={`mailto:${companyInfo.email}`} className="hover:text-cyan-300">{companyInfo.email}</a></div>
                <div className="flex items-start gap-3"><Phone className="h-5 w-5 text-cyan-400 mt-0.5" /><a href={`tel:${companyInfo.phone}`} className="hover:text-cyan-300">{companyInfo.phone}</a></div>
              </div>
            </div>
            <div className="hidden md:block">
              <PhotoCard src={IMG.piping} alt="Industrial process piping" />
            </div>
          </div>
        </BrochurePage>

        {/* ============================================== */}
        {/* PAGE 2 — MISSION                                */}
        {/* ============================================== */}
        <BrochurePage pageNo={2} totalPages={TOTAL} testId="page-mission">
          <div className="max-w-3xl">
            <PageTitle>Our Mission</PageTitle>
            <div className="text-2xl md:text-3xl font-light leading-relaxed text-slate-700 italic relative pl-6 border-l-4 border-cyan-500">
              "To deliver reliable, safe, and cost-effective engineering and construction
              solutions across the energy, utilities, and infrastructure sectors —
              exceeding client expectations while fostering innovation, operational
              excellence, and sustainability."
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              {['Reliability', 'Safety', 'Cost-Effectiveness', 'Innovation', 'Sustainability'].map((t) => (
                <span key={t} className="px-4 py-1.5 bg-cyan-50 text-cyan-700 border border-cyan-200 rounded-full text-sm font-medium">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </BrochurePage>

        {/* ============================================== */}
        {/* PAGE 3 — BUSINESS SERVICES                      */}
        {/* ============================================== */}
        <BrochurePage pageNo={3} totalPages={TOTAL} testId="page-business-services">
          <PageTitle>Our Business Services</PageTitle>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
            {businessServices.map((s, idx) => {
              const Icon = s.icon;
              return (
                <div key={idx} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all" data-testid={`biz-service-${idx}`}>
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4 shadow-md">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <p className="text-slate-800 font-semibold leading-snug">{s.title}</p>
                </div>
              );
            })}
          </div>
        </BrochurePage>

        {/* ============================================== */}
        {/* PAGE 4 — MAJOR CLIENTS                          */}
        {/* ============================================== */}
        <BrochurePage pageNo={4} totalPages={TOTAL} testId="page-clients">
          <PageTitle>Our Major Clients</PageTitle>
          <p className="text-slate-600 mb-10 max-w-3xl">Trusted partner to leading names across Singapore's energy, utilities and infrastructure sectors.</p>
          <div className="grid sm:grid-cols-2 gap-6">
            {clients.map((c, idx) => {
              const Icon = c.icon;
              return (
                <div key={idx} className="bg-white border-2 border-slate-200 hover:border-cyan-500 rounded-xl p-6 flex items-center gap-5 transition-all shadow-sm hover:shadow-lg" data-testid={`major-client-${idx}`}>
                  <div className="w-16 h-16 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="h-8 w-8 text-cyan-700" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">{c.name}</h3>
                    <p className="text-sm text-slate-500">{c.sector}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </BrochurePage>

        {/* ============================================== */}
        {/* PAGE 5 — CERTIFICATIONS                         */}
        {/* ============================================== */}
        <BrochurePage pageNo={5} totalPages={TOTAL} testId="page-certifications">
          <PageTitle>Membership & Certifications</PageTitle>
          <p className="text-slate-600 mb-10 max-w-3xl">
            We work to internationally recognised workplace safety standards — backed by ongoing certification and training under Singapore's bizSAFE programme.
          </p>
          <div className="max-w-md">
            {[
              { label: 'bizSAFE', sub: 'Workplace Safety & Health Programme', icon: ClipboardCheck },
            ].map((c, idx) => {
              const Icon = c.icon;
              return (
                <div key={idx} className="bg-white border-2 border-slate-200 rounded-xl p-8 text-center hover:shadow-xl transition-all" data-testid={`cert-${idx}`}>
                  <div className="w-24 h-24 mx-auto bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mb-5 shadow-lg">
                    <Icon className="h-12 w-12 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">{c.label}</h3>
                  <p className="text-sm text-slate-500 mt-2">{c.sub}</p>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 mt-10 italic">Certification documentation available upon request.</p>
        </BrochurePage>

        {/* ============================================== */}
        {/* PAGE 6 — COMMITMENT QUOTE                       */}
        {/* ============================================== */}
        <BrochurePage pageNo={6} totalPages={TOTAL} testId="page-commitment">
          <div className="max-w-3xl">
            <PageTitle>Our Commitment</PageTitle>
            <div className="text-xl md:text-2xl font-light leading-relaxed text-slate-700 italic relative pl-6 border-l-4 border-amber-400">
              "Our team is committed to delivering the best solutions, continuity of
              service, competitively priced options, and uncompromised quality —
              upholding the highest standards of safety, environmental stewardship,
              and regulatory compliance on every project we undertake."
            </div>
            <div className="mt-12 grid sm:grid-cols-3 gap-6">
              <div className="bg-cyan-50 rounded-xl p-5 border border-cyan-100">
                <Heart className="h-6 w-6 text-cyan-600 mb-2" />
                <h4 className="font-bold text-slate-900">Customer-First</h4>
                <p className="text-sm text-slate-600 mt-1">Continuity, responsiveness, accountability.</p>
              </div>
              <div className="bg-cyan-50 rounded-xl p-5 border border-cyan-100">
                <Target className="h-6 w-6 text-cyan-600 mb-2" />
                <h4 className="font-bold text-slate-900">Risk Mitigation</h4>
                <p className="text-sm text-slate-600 mt-1">Plan first, build safely, finish strong.</p>
              </div>
              <div className="bg-cyan-50 rounded-xl p-5 border border-cyan-100">
                <Sparkles className="h-6 w-6 text-cyan-600 mb-2" />
                <h4 className="font-bold text-slate-900">Innovation</h4>
                <p className="text-sm text-slate-600 mt-1">Efficient methods, modern tooling.</p>
              </div>
            </div>
          </div>
        </BrochurePage>

        {/* ============================================== */}
        {/* PAGE 7 — CORE SERVICES                          */}
        {/* ============================================== */}
        <BrochurePage pageNo={7} totalPages={TOTAL} testId="page-core-services">
          <PageTitle>Core Services</PageTitle>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl">
            {coreServices.map((s, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg border border-slate-200 shadow-sm" data-testid={`core-${idx}`}>
                <CheckCircle className="h-5 w-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">{s}</span>
              </div>
            ))}
          </div>
        </BrochurePage>

        {/* ============================================== */}
        {/* PAGE 8 — PROCESS CONSTRUCTION & CIVIL WORKS     */}
        {/* ============================================== */}
        <BrochurePage pageNo={8} totalPages={TOTAL} testId="page-civil-works">
          <PageTitle>Process Construction & Civil Works</PageTitle>
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <p className="text-slate-600 mb-6">
                Turnkey construction for industrial plants and facilities — from site preparation and structural steelwork to piping installation and full system integration.
              </p>
              <ul className="space-y-3">
                {[
                  'Excavation & underground piping works',
                  'Structural steelwork & equipment foundations',
                  'Piping fabrication & installation',
                  'Coating, painting & insulation works',
                  'Hacking & demolition works',
                  'System integration & commissioning',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <PhotoCard src={IMG.worker} alt="On-site welding & fabrication" caption="On-site welding & fabrication" className="col-span-2" />
              <PhotoCard src={IMG.piping} alt="Process piping" caption="Process piping" />
              <PhotoCard src={IMG.crew} alt="Equipment erection" caption="Equipment erection" />
            </div>
          </div>
        </BrochurePage>

        {/* ============================================== */}
        {/* PAGE 9 — MEP / MAINTENANCE WORKS                */}
        {/* ============================================== */}
        <BrochurePage pageNo={9} totalPages={TOTAL} testId="page-mep-works">
          <PageTitle>MEP & Maintenance Works</PageTitle>
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div className="md:order-2">
              <p className="text-slate-600 mb-6">
                Mechanical, electrical and plumbing scope delivered to industrial standards — with preventive, predictive, and shutdown maintenance support to keep critical assets running.
              </p>
              <ul className="space-y-3">
                {[
                  'Mechanical equipment erection',
                  'Piping fabrication & installation',
                  'Welding & structural supports',
                  'HVAC system works',
                  'Preventive & predictive maintenance',
                  'Shutdown services for oil & chemical plants',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:order-1 grid grid-cols-2 gap-4">
              <PhotoCard src={IMG.crew} alt="Plant maintenance" caption="Plant maintenance" className="col-span-2" />
              <PhotoCard src={IMG.piping} alt="Piping system" caption="Piping system" />
              <PhotoCard src={IMG.worker} alt="MEP installation" caption="MEP installation" />
            </div>
          </div>
        </BrochurePage>

        {/* ============================================== */}
        {/* PAGE 10 — ELECTRICAL WORKS                      */}
        {/* ============================================== */}
        <BrochurePage pageNo={10} totalPages={TOTAL} testId="page-electrical-works">
          <PageTitle>Electrical Works</PageTitle>
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <p className="text-slate-600 mb-6">
                Full-scope electrical and instrumentation installation by certified electricians — from cable supports through final termination, testing, and earthing protection.
              </p>
              <ul className="space-y-3">
                {[
                  'Electrical & ELV cable supports',
                  'Electrical & ELV cable laying',
                  'Cable dressing & termination',
                  'Earthing & lightning protection',
                  'Electrical equipment installation',
                  'Instrumentation system testing',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <Users className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">
                  <span className="font-bold">Manpower Supply:</span> Certified electricians, welders, pipefitters, instrument technicians and site supervisors — vetted and safety-trained.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <PhotoCard src={IMG.electrical} alt="Electrical cable tray" caption="Cable tray installation" />
              <PhotoCard src={IMG.crew} alt="Skilled team" caption="Skilled manpower" />
            </div>
          </div>
        </BrochurePage>

        {/* ============================================== */}
        {/* PAGE 11 — KEY PROJECTS                          */}
        {/* ============================================== */}
        <BrochurePage pageNo={11} totalPages={TOTAL} dark testId="page-projects">
          <PageTitle dark>Key Projects</PageTitle>
          <p className="text-amber-200/80 mb-8 font-medium tracking-wide">Projects 2024 – 2026</p>
          <div className="overflow-x-auto rounded-xl border border-slate-700 bg-slate-900/60 backdrop-blur">
            <table className="w-full text-sm md:text-base">
              <thead className="bg-slate-800/80">
                <tr className="text-left text-cyan-300 uppercase text-xs tracking-wider">
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">Project Title</th>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3 hidden md:table-cell">Work Scope</th>
                  <th className="px-4 py-3">Duration</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {projects.map((p) => (
                  <tr key={p.no} className="hover:bg-slate-800/40 transition-colors" data-testid={`project-row-${p.no}`}>
                    <td className="px-4 py-3 text-slate-400 font-mono">{String(p.no).padStart(2, '0')}</td>
                    <td className="px-4 py-3 font-semibold text-white">{p.title}</td>
                    <td className="px-4 py-3 text-slate-300">{p.client}</td>
                    <td className="px-4 py-3 text-slate-400 hidden md:table-cell">{p.scope}</td>
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{p.duration}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                        p.status === 'Completed' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' :
                        p.status === 'Ongoing' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' :
                        'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                      }`}>{p.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </BrochurePage>

        {/* CTA — outside paginated brochure */}
        <section className="bg-gradient-to-br from-cyan-600 to-blue-700 text-white py-20 px-6">
          <div className="container mx-auto max-w-5xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Partner With Sky Control</h2>
            <p className="text-base md:text-lg text-cyan-50 max-w-3xl mx-auto mb-10">
              For project enquiries, maintenance contracts or manpower supply — speak to our team today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/#contact" data-testid="profile-cta-quote">
                <Button size="lg" className="bg-white text-cyan-700 hover:bg-cyan-50 text-lg px-8 py-6 w-full">
                  Request a Quote
                </Button>
              </Link>
              <a href="https://wa.me/6581130354" target="_blank" rel="noopener noreferrer" data-testid="profile-cta-whatsapp">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 w-full">
                  <MessageCircle className="mr-2 h-5 w-5" /> Chat on WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>

        <footer className="bg-slate-950 text-slate-400 py-8 px-4 text-center text-sm">
          <p>© 2026 Sky Control Solution & Trading Pte Ltd. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default Profile;
